import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import cx from "classnames";
import { Slot } from "@radix-ui/react-slot";
import { Link } from "@/features/shell/components/shared/Link/Link";
import { Skeleton } from "@picklebet/ui";
import { renderChildrenWithLoading } from "utilities/dom";

import * as styles from "./List.module.scss";

type ListProps = {
  children: React.ReactNode;
  asChild?: boolean;
  loading?: boolean;
};

type ListItemProps = {
  icon?: React.ReactNode;
  label: React.ReactNode;
  value?: string;
  description?: React.ReactNode;
  children?: React.ReactNode;
  disabled?: boolean;
  to?: string;
  loading?: boolean;
  onClick?: () => void;
  className?: string;
  labelClassName?: string;
};

type ListBadgeProps = {
  children: React.ReactNode;
  icon?: React.ReactNode;
} & VariantProps<typeof badgeVariants>;

export const badgeVariants = cva(styles.badge, {
  variants: {
    variant: {
      danger: styles.danger,
      warning: styles.warning,
      info: styles.info,
      success: styles.success,
      neutral: styles.neutral,
    },
  },
  defaultVariants: {
    variant: "info",
  },
});

const ListItem = ({
  icon,
  label,
  value,
  description,
  children,
  disabled,
  to,
  onClick,
  loading,
  className: classNameProp,
  labelClassName,
}: ListItemProps) => {
  const Component = to ? Link : "li";
  const toProps = to ? { to } : {};

  const className = cx(styles.item, classNameProp, {
    [styles.disabled]: disabled,
    [styles.pointer]: !!onClick,
    [styles.withDescription]: !!description,
  });
  if (loading) return <Skeleton className={className} />;

  return (
    // NOTE: This is a hack to get around reach router link ts issues
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <Component className={className} onClick={onClick} {...toProps}>
      {icon && <span className={styles.icon}>{icon}</span>}
      {description ? (
        <span className={styles.content}>
          <span className={styles.heading}>
            <span className={cx(styles.label, labelClassName)}>{label}</span>
            {value && <span className={styles.value}>{value}</span>}
          </span>
          <span className={styles.description}>{description}</span>
        </span>
      ) : (
        <>
          <span className={cx(styles.label, labelClassName)}>{label}</span>
          {value && <span className={styles.value}>{value}</span>}
        </>
      )}
      {children ? children : null}
    </Component>
  );
};

const List = ({ children, asChild, loading = false }: ListProps) => {
  const Component = asChild ? Slot : "ul";
  return (
    <Component className={styles.list}>
      {renderChildrenWithLoading(children, loading)}
    </Component>
  );
};

const ListBadge = ({ children, icon, variant }: ListBadgeProps) => {
  return (
    <span className={badgeVariants({ variant })}>
      {icon && <span className={styles.icon}>{icon}</span>}
      {children}
    </span>
  );
};

export { List, ListItem, ListBadge };
